import type { ReactNode } from 'react';
import { Panel } from '../../kit';
import { Failure, Loading } from '../../bits';
import type { ReadState } from '../../context';
import { useLocale, useT } from '../../../messages';
import type { ActivityPage } from '../../../wire';
import { sentenceOf } from '../activity/sentences';
import { shortDate } from './RightNow';

/** How many lines "Lately" shows — and so how many rows it asks for. */
export const LATELY_LINES = 5;

/**
 * **"Lately"** — the last few things that changed, as sentences, with the way
 * to the whole log. The read is the Activity tab's own (`GET …/activity`), so
 * a line here and a row there are the same audit row, worded by the same
 * mapping (`activity/sentences.ts`).
 *
 * **Reads are not news.** Every operator read is itself an audit row, so the
 * trail is mostly somebody looking — opening the Overview alone writes three.
 * The read asks the server for changes only (`changes_only=true`, the list of
 * looks being the server's registry), so five rows are five changes and no
 * copy of that list lives here.
 */
export function Lately({
  activity,
  onAll,
}: {
  activity: ReadState<ActivityPage>;
  onAll: () => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const page = activity.value;
  const lines = page === null ? [] : page.entries.slice(0, LATELY_LINES);

  return (
    <Panel
      title={t('overview.lately.title')}
      className="ov-lately"
      actions={
        <button type="button" className="button button--quiet button--sm" onClick={onAll}>
          {t('overview.lately.all')}
        </button>
      }
    >
      {activity.failure !== null ? <Failure failure={activity.failure} /> : null}
      {page === null ? (
        activity.loading ? (
          <Loading what={t('overview.loading.lately')} />
        ) : null
      ) : lines.length === 0 ? (
        <p className="muted">{t('overview.lately.empty')}</p>
      ) : (
        <ul className="ov-feed">
          {lines.map((e) => {
            const s = sentenceOf(t, locale, {
              action: e.action,
              actor: e.actor,
              target: e.target,
              detail: e.summary,
            });
            return (
              <li key={e.audit_id}>
                <time dateTime={e.at}>{shortDate(e.at, locale)}</time>
                <span>
                  <b>{s.who}</b> {s.what}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
